import type { Landmarks, Vec2 } from '@store/handStore';

/**
 * MediaPipe HandLandmarker indices. 21 landmarks per hand.
 * See https://ai.google.dev/edge/mediapipe/solutions/vision/hand_landmarker
 */
export const LM = {
  WRIST: 0,
  THUMB_CMC: 1,
  THUMB_MCP: 2,
  THUMB_IP: 3,
  THUMB_TIP: 4,
  INDEX_MCP: 5,
  INDEX_PIP: 6,
  INDEX_DIP: 7,
  INDEX_TIP: 8,
  MIDDLE_MCP: 9,
  MIDDLE_PIP: 10,
  MIDDLE_DIP: 11,
  MIDDLE_TIP: 12,
  RING_MCP: 13,
  RING_PIP: 14,
  RING_DIP: 15,
  RING_TIP: 16,
  PINKY_MCP: 17,
  PINKY_PIP: 18,
  PINKY_DIP: 19,
  PINKY_TIP: 20,
} as const;

export type FingerName = 'thumb' | 'index' | 'middle' | 'ring' | 'pinky';

export const FINGER_TIPS: Record<FingerName, number> = {
  thumb: LM.THUMB_TIP,
  index: LM.INDEX_TIP,
  middle: LM.MIDDLE_TIP,
  ring: LM.RING_TIP,
  pinky: LM.PINKY_TIP,
};

export const FINGER_MCPS: Record<FingerName, number> = {
  thumb: LM.THUMB_CMC,
  index: LM.INDEX_MCP,
  middle: LM.MIDDLE_MCP,
  ring: LM.RING_MCP,
  pinky: LM.PINKY_MCP,
};

export const FINGER_PIPS: Record<FingerName, number> = {
  thumb: LM.THUMB_IP,
  index: LM.INDEX_PIP,
  middle: LM.MIDDLE_PIP,
  ring: LM.RING_PIP,
  pinky: LM.PINKY_PIP,
};

export const dist = (a: Vec2, b: Vec2): number => {
  const dx = a.x - b.x;
  const dy = a.y - b.y;
  return Math.hypot(dx, dy);
};

/** Average of several landmarks. */
export const centroid = (lm: Landmarks, indices: readonly number[]): Vec2 => {
  let sx = 0,
    sy = 0;
  for (const i of indices) {
    sx += lm[i]!.x;
    sy += lm[i]!.y;
  }
  return { x: sx / indices.length, y: sy / indices.length };
};

/** Palm center — centroid of wrist + four knuckles. */
export const palmCenter = (lm: Landmarks): Vec2 =>
  centroid(lm, [
    LM.WRIST,
    LM.INDEX_MCP,
    LM.MIDDLE_MCP,
    LM.RING_MCP,
    LM.PINKY_MCP,
  ]);

/** Approximate palm width for scaling. */
export const palmWidth = (lm: Landmarks): number =>
  dist(lm[LM.INDEX_MCP]!, lm[LM.PINKY_MCP]!);

/**
 * Per-finger "curl" ratio in [0, ~1].
 *
 * 0  = fully extended (tip far from palm, tip—MCP roughly aligned to palm—MCP)
 * 1+ = fully curled (tip close to palm or past MCP line)
 *
 * Implementation: ratio of (tip→palm) over (MCP→palm), inverted.
 */
export const fingerCurl = (lm: Landmarks, finger: FingerName): number => {
  const palm = palmCenter(lm);
  const tip = lm[FINGER_TIPS[finger]]!;
  const mcp = lm[FINGER_MCPS[finger]]!;
  const tipToPalm = dist(tip, palm);
  const mcpToPalm = dist(mcp, palm);
  if (mcpToPalm < 1e-6) return 0;
  return 1 - tipToPalm / (mcpToPalm * 1.8);
};

/** Mean curl of index+middle+ring+pinky (ignoring thumb which behaves differently). */
export const meanCurl = (lm: Landmarks): number => {
  const fingers: FingerName[] = ['index', 'middle', 'ring', 'pinky'];
  let sum = 0;
  for (const f of fingers) sum += fingerCurl(lm, f);
  return sum / fingers.length;
};
